import React, { useEffect, useState } from "react";
import { apiClient } from "../services/api/apiClient";
import { useAuth } from "../components/Contexts/AuthContext";
import {
  BsLightningChargeFill,
  BsBuilding,
  BsPeople,
  BsHddNetwork,
  BsShieldCheck,
  BsBoxArrowRight,
  BsArrowLeft,
  BsChevronRight,
  BsPlusLg,
  BsXLg,
} from "react-icons/bs";
import { FaUserShield, FaUser } from "react-icons/fa";
import Spinner from "react-bootstrap/Spinner";
import OrgBillingPanel, { describeBilling } from "../components/OrgBillingPanel";

interface OrgRow {
  id: number;
  name: string;
  friendlyname: string;
  system_count: number;
  online_user_count: number;
  monthly_price_cents: number;
  billing_status?: string;
  billing_override?: string;
  billing_entitled?: boolean;
  billing_reason?: string;
  trial_ends_at?: string | null;
  created_at: string;
}

interface OrgUser {
  sub: string;
  username: string;
  fullname: string;
  phonenumber: string;
  role: string;
  created_at: string;
}

interface OrgSystem {
  id: string;
  system_name: string;
  has_study: boolean;
  status: string;
  claimedby: string;
  claimedby_name: string;
  last_checked: string;
  ping: string;
  network_type: string;
  version: string;
}

interface OrgDetail {
  org: {
    id: number;
    name: string;
    friendlyname: string;
    created_at: string;
    system_count: number;
    online_user_count: number;
  };
  users: OrgUser[];
  systems: OrgSystem[];
}

// Platform-owner dashboard. Loads at admin.epicarealert.com when the
// logged-in user has role "superadmin." Self-contained page — no shared
// NavBar, no sidebars — uses the standard EpicareAlert visual theme
// (light backgrounds, brand blue gradients) so it feels like part of the
// product rather than a foreign admin panel.
const SuperAdminDashboard: React.FC = () => {
  const auth = useAuth();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-in state: null = orgs list view; non-null = detail view for that
  // org. Detail data is loaded on demand when an org is clicked.
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [orgDetail, setOrgDetail] = useState<OrgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Add Organization modal state.
  const [showAddOrg, setShowAddOrg] = useState(false);

  const loadOrgs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/api/v1/sa/orgs");
      if (!res) return;
      if (!res.ok) {
        setError(`Failed to load orgs: ${res.status}`);
        return;
      }
      const data = await res.json();
      setOrgs(data.orgs || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load orgs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrgs();
  }, []);

  const openOrg = async (orgId: number) => {
    setSelectedOrgId(orgId);
    setDetailLoading(true);
    setDetailError(null);
    setOrgDetail(null);
    try {
      const res = await apiClient.get(`/api/v1/sa/orgs/${orgId}/detail`);
      if (!res) return;
      if (!res.ok) {
        setDetailError(`Failed to load org: ${res.status}`);
        return;
      }
      const data = await res.json();
      setOrgDetail(data);
    } catch (e: any) {
      setDetailError(e?.message || "Failed to load org");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeOrg = () => {
    setSelectedOrgId(null);
    setOrgDetail(null);
    setDetailError(null);
  };

  const formatLastChecked = (ms: string) => {
    if (!ms) return "—";
    const n = Number(ms);
    if (!Number.isFinite(n)) return "—";
    const ago = Date.now() - n;
    const mins = Math.floor(ago / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const formatPrice = (cents: number) =>
    cents === 0 ? "—" : `$${(cents / 100).toFixed(2)}/mo`;

  const forceUpdateOrg = async (org: OrgRow) => {
    if (!window.confirm(
      `Force-update all ${org.system_count} systems in ${org.friendlyname}? ` +
      `Each system will download and install the latest installer immediately.`,
    )) return;
    try {
      const res = await apiClient.post(`/api/v1/sa/orgs/${org.id}/force-update`, {});
      if (!res) return;
      const data = await res.json().catch(() => ({}));
      alert(data.message || (res.ok ? "Triggered" : `Failed: ${res.status}`));
    } catch (e: any) {
      alert(`Failed: ${e.message}`);
    }
  };

  const forceUpdateAll = async () => {
    const totalSystems = orgs.reduce((sum, o) => sum + o.system_count, 0);
    if (!window.confirm(
      `Force-update EVERY system across ALL orgs? ${totalSystems} systems in ${orgs.length} orgs. ` +
      `This is the nuclear button. Continue?`,
    )) return;
    try {
      const res = await apiClient.post("/api/v1/sa/force-update-all", {});
      if (!res) return;
      const data = await res.json().catch(() => ({}));
      alert(data.message || (res.ok ? "Triggered" : `Failed: ${res.status}`));
    } catch (e: any) {
      alert(`Failed: ${e.message}`);
    }
  };

  const totalMonthlyCents = orgs.reduce((s, o) => s + (o.monthly_price_cents || 0), 0);
  const totalSystems = orgs.reduce((s, o) => s + o.system_count, 0);
  const totalOnline = orgs.reduce((s, o) => s + o.online_user_count, 0);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f1f5f9 0%, #e8eef7 100%)",
        color: "#1e293b",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar — mirrors the regular NavBar visually so the super-admin
          shell still feels like EpicareAlert, just a different "wing." */}
      <header
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(33, 150, 243, 0.1)",
          boxShadow: "0 2px 20px rgba(0, 0, 0, 0.05)",
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #2196F3, #1976D2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
            }}
          >
            <BsShieldCheck size={18} color="white" />
          </div>
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: "18px",
                background: "linear-gradient(135deg, #2196F3, #1976D2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.3px",
              }}
            >
              EpiCareAlert
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
                marginTop: "-2px",
              }}
            >
              Platform Console
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {auth.user && (
            <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
              {auth.user.fullname}
            </span>
          )}
          <button
            onClick={() => auth.logout()}
            style={{
              background: "rgba(33, 150, 243, 0.08)",
              border: "1px solid rgba(33, 150, 243, 0.2)",
              color: "#1565C0",
              padding: "7px 14px",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <BsBoxArrowRight size={13} /> Sign out
          </button>
        </div>
      </header>

      {/* Content */}
      <main style={{ flex: 1, padding: "32px", overflow: "auto" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {selectedOrgId !== null ? (
            <OrgDetailView
              loading={detailLoading}
              error={detailError}
              detail={orgDetail}
              onBack={closeOrg}
              formatLastChecked={formatLastChecked}
            />
          ) : (
          <>
          {/* Summary stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "28px" }}>
            {[
              { label: "Organizations", value: orgs.length, icon: <BsBuilding size={20} />, color: "#2196F3" },
              { label: "Systems", value: totalSystems, icon: <BsHddNetwork size={20} />, color: "#1976D2" },
              { label: "Operators online", value: totalOnline, icon: <BsPeople size={20} />, color: "#10b981" },
              { label: "Monthly revenue", value: `$${(totalMonthlyCents / 100).toFixed(2)}`, icon: <BsLightningChargeFill size={20} />, color: "#f59e0b" },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(33, 150, 243, 0.1)",
                  borderRadius: "16px",
                  padding: "20px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#64748b", fontSize: "11px", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
                  <span style={{ color: stat.color }}>{stat.icon}</span>
                  {stat.label}
                </div>
                <div style={{ fontSize: "28px", fontWeight: 700, color: "#1e293b" }}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Section header + global actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 600, margin: 0, color: "#1e293b" }}>Organizations</h2>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowAddOrg(true)}
                style={{
                  background: "linear-gradient(135deg, #2196F3, #1976D2)",
                  border: "none",
                  color: "white",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 14px rgba(33, 150, 243, 0.3)",
                }}
              >
                <BsPlusLg size={13} />
                Add Organization
              </button>
              <button
                onClick={forceUpdateAll}
                disabled={orgs.length === 0 || loading}
                style={{
                  background: orgs.length === 0 ? "#fca5a5" : "linear-gradient(135deg, #dc2626, #b91c1c)",
                  border: "none",
                  color: "white",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: orgs.length === 0 ? "not-allowed" : "pointer",
                  opacity: orgs.length === 0 ? 0.5 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: orgs.length === 0 ? "none" : "0 4px 14px rgba(220, 38, 38, 0.3)",
                }}
                title="Force update every system across every organization"
              >
                <BsLightningChargeFill size={13} />
                Force Update All Systems
              </button>
            </div>
          </div>

          {/* Orgs table */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
              <Spinner animation="border" /> Loading orgs...
            </div>
          ) : error ? (
            <div
              style={{
                padding: "20px",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: "12px",
                color: "#dc2626",
              }}
            >
              {error}
            </div>
          ) : orgs.length === 0 ? (
            <div
              style={{
                padding: "60px",
                textAlign: "center",
                background: "rgba(255,255,255,0.6)",
                border: "1px dashed rgba(33, 150, 243, 0.2)",
                borderRadius: "16px",
                color: "#64748b",
              }}
            >
              No organizations yet.
            </div>
          ) : (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(33, 150, 243, 0.1)",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, rgba(33,150,243,0.06), rgba(25,118,210,0.04))" }}>
                    <th style={th}>Organization</th>
                    <th style={th}>Subdomain</th>
                    <th style={{ ...th, textAlign: "right" }}>Systems</th>
                    <th style={{ ...th, textAlign: "right" }}>Online</th>
                    <th style={{ ...th, textAlign: "right" }}>Billing</th>
                    <th style={{ ...th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org) => (
                    <tr
                      key={org.id}
                      onClick={() => openOrg(org.id)}
                      style={{ borderTop: "1px solid rgba(33,150,243,0.06)", cursor: "pointer", transition: "background 0.15s ease" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(33,150,243,0.04)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: "#1e293b", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          {org.friendlyname}
                          <BsChevronRight size={11} color="#94a3b8" />
                        </div>
                        <div style={{ fontSize: "11px", color: "#94a3b8" }}>Org #{org.id}</div>
                      </td>
                      <td style={{ ...td, color: "#64748b", fontFamily: "monospace", fontSize: "13px" }}>
                        {org.name}.epicarealert.com
                      </td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                        {org.system_count}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            color: org.online_user_count > 0 ? "#10b981" : "#94a3b8",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: org.online_user_count > 0 ? "#10b981" : "#cbd5e1",
                            }}
                          />
                          {org.online_user_count}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: org.monthly_price_cents === 0 ? "#94a3b8" : "#1e293b", fontWeight: 600 }}>
                        {(() => {
                          const b = describeBilling({
                            entitled: org.billing_entitled ?? true,
                            reason: org.billing_reason || "",
                            status: org.billing_status,
                            override: org.billing_override,
                          });
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
                              <span style={{ background: `${b.color}1a`, color: b.color, padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700 }}>
                                {b.label}
                              </span>
                              {formatPrice(org.monthly_price_cents)}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            forceUpdateOrg(org);
                          }}
                          disabled={org.system_count === 0}
                          style={{
                            background: "rgba(220,38,38,0.08)",
                            border: "1px solid rgba(220,38,38,0.25)",
                            color: "#dc2626",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: 600,
                            cursor: org.system_count === 0 ? "not-allowed" : "pointer",
                            opacity: org.system_count === 0 ? 0.4 : 1,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          title="Force update all systems in this org"
                        >
                          <BsLightningChargeFill size={10} />
                          Force Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </div>
      </main>

      {showAddOrg && (
        <AddOrgModal
          onClose={() => setShowAddOrg(false)}
          onCreated={() => {
            setShowAddOrg(false);
            loadOrgs();
          }}
        />
      )}
    </div>
  );
};

// Modal for creating a new organization + its first power user. Posts
// to /api/v1/sa/orgs which does the org row + Cognito user + Postgres
// user atomically (with rollback on partial failure).
interface AddOrgModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const AddOrgModal: React.FC<AddOrgModalProps> = ({ onClose, onCreated }) => {
  const [subdomain, setSubdomain] = useState("");
  const [friendlyname, setFriendlyname] = useState("");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phonenumber, setPhonenumber] = useState("+1");
  const [password, setPassword] = useState("");
  const [trialDays, setTrialDays] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiClient.post("/api/v1/sa/orgs", {
        subdomain: subdomain.trim().toLowerCase(),
        friendlyname: friendlyname.trim(),
        trialDays: trialDays.trim() === "" ? undefined : Number(trialDays),
        firstUser: {
          fullname: fullname.trim(),
          email: email.trim().toLowerCase(),
          phonenumber: phonenumber.trim(),
          password,
        },
      });
      if (!res) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || `Failed: ${res.status}`);
        return;
      }
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 3000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          maxWidth: "520px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Modal header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
              Add Organization
            </h3>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
              Creates the org + its first power user
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748b",
              cursor: "pointer",
              padding: "4px",
              fontSize: "16px",
            }}
          >
            <BsXLg />
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: "20px 24px" }}>
          {error && (
            <div
              style={{
                padding: "10px 14px",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: "8px",
                color: "#dc2626",
                fontSize: "13px",
                marginBottom: "16px",
              }}
            >
              {error}
            </div>
          )}

          <SectionLabel>Organization</SectionLabel>
          <Field label="Subdomain" hint="3-32 chars, lowercase letters/numbers/hyphens. Becomes [name].epicarealert.com">
            <input
              required
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder="example"
              disabled={submitting}
              style={inputStyle}
              autoFocus
            />
          </Field>
          <Field label="Display name" hint="Shown in the dashboard header. Can include spaces and punctuation.">
            <input
              required
              value={friendlyname}
              onChange={(e) => setFriendlyname(e.target.value)}
              placeholder="Example Hospital Group"
              disabled={submitting}
              style={inputStyle}
            />
          </Field>
          <Field label="Free trial (days)" hint="Leave blank for one month. 0 means no trial: they must subscribe before adding systems.">
            <input
              type="number"
              min={0}
              max={365}
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              placeholder="1 month"
              disabled={submitting}
              style={inputStyle}
            />
          </Field>

          <SectionLabel style={{ marginTop: "20px" }}>First Power User</SectionLabel>
          <Field label="Full name">
            <input
              required
              value={fullname}
              onChange={(e) => setFullname(e.target.value)}
              placeholder="Jane Doe"
              disabled={submitting}
              style={inputStyle}
            />
          </Field>
          <Field label="Email">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              disabled={submitting}
              style={inputStyle}
            />
          </Field>
          <Field label="Phone" hint="E.164 format (e.g. +15551234567)">
            <input
              required
              value={phonenumber}
              onChange={(e) => setPhonenumber(e.target.value)}
              placeholder="+15551234567"
              disabled={submitting}
              style={inputStyle}
            />
          </Field>
          <Field label="Temporary password" hint="They'll set their own via Forgot Password on first login.">
            <input
              required
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tmp!password1"
              disabled={submitting}
              minLength={8}
              style={inputStyle}
            />
          </Field>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                background: "transparent",
                border: "1px solid #cbd5e1",
                color: "#475569",
                padding: "9px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: submitting ? "#94a3b8" : "linear-gradient(135deg, #2196F3, #1976D2)",
                border: "none",
                color: "white",
                padding: "9px 18px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: submitting ? "none" : "0 4px 14px rgba(33, 150, 243, 0.3)",
              }}
            >
              {submitting ? (
                <><Spinner as="span" animation="border" size="sm" /> Creating...</>
              ) : (
                <><BsPlusLg size={12} /> Create Organization</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div
    style={{
      fontSize: "11px",
      color: "#1565C0",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      fontWeight: 700,
      marginBottom: "10px",
      ...style,
    }}
  >
    {children}
  </div>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div style={{ marginBottom: "12px" }}>
    <label style={{ display: "block", fontSize: "12px", color: "#374151", fontWeight: 600, marginBottom: "4px" }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>{hint}</div>}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "13px",
  color: "#1e293b",
  outline: "none",
  background: "#ffffff",
};

// Drill-in view for a single org. Two tables — users + systems — with the
// same visual theme as the orgs list above. Back arrow returns to list.
interface OrgDetailViewProps {
  loading: boolean;
  error: string | null;
  detail: OrgDetail | null;
  onBack: () => void;
  formatLastChecked: (ms: string) => string;
}

const OrgDetailView: React.FC<OrgDetailViewProps> = ({
  loading,
  error,
  detail,
  onBack,
  formatLastChecked,
}) => {
  const statusColor = (status: string) => {
    if (status === "Offline") return "#ef4444";
    if (status === "invalidHeadbox") return "#f59e0b";
    if (status === "neuroworksClosed") return "#f59e0b";
    if (status === "RUN_CHECK" || status === "awaiting_approval") return "#3b82f6";
    return "#10b981";
  };

  return (
    <>
      <button
        onClick={onBack}
        style={{
          background: "transparent",
          border: "none",
          color: "#1565C0",
          padding: 0,
          marginBottom: "20px",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <BsArrowLeft size={14} /> Back to organizations
      </button>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
          <Spinner animation="border" /> Loading organization...
        </div>
      ) : error ? (
        <div
          style={{
            padding: "20px",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: "12px",
            color: "#dc2626",
          }}
        >
          {error}
        </div>
      ) : !detail ? null : (
        <>
          {/* Org header */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(33, 150, 243, 0.1)",
              borderRadius: "16px",
              padding: "24px",
              marginBottom: "20px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontSize: "11px", color: "#1565C0", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: "4px" }}>
                Organization · #{detail.org.id}
              </div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b", marginBottom: "4px" }}>
                {detail.org.friendlyname}
              </div>
              <div style={{ fontSize: "13px", color: "#64748b", fontFamily: "monospace" }}>
                {detail.org.name}.epicarealert.com
              </div>
            </div>
            <div style={{ display: "flex", gap: "24px", textAlign: "center" }}>
              <div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#1e293b" }}>{detail.systems.length}</div>
                <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Systems</div>
              </div>
              <div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: "#1e293b" }}>{detail.users.length}</div>
                <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Users</div>
              </div>
              <div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: detail.org.online_user_count > 0 ? "#10b981" : "#94a3b8" }}>
                  {detail.org.online_user_count}
                </div>
                <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Online</div>
              </div>
            </div>
          </div>

          <OrgBillingPanel orgId={detail.org.id} />

          {/* Users table */}
          <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: "0 0 12px 4px" }}>
            Users
          </h3>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(33, 150, 243, 0.1)",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
              marginBottom: "24px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, rgba(33,150,243,0.06), rgba(25,118,210,0.04))" }}>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Role</th>
                </tr>
              </thead>
              <tbody>
                {detail.users.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...td, textAlign: "center", color: "#94a3b8" }}>
                      No users in this org yet.
                    </td>
                  </tr>
                ) : (
                  detail.users.map((u) => (
                    <tr key={u.sub} style={{ borderTop: "1px solid rgba(33,150,243,0.06)" }}>
                      <td style={td}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: "#1e293b", fontWeight: 600 }}>
                          {u.role === "admin" || u.role === "superadmin" ? (
                            <FaUserShield color="#10b981" size={13} />
                          ) : (
                            <FaUser color="#94a3b8" size={12} />
                          )}
                          {u.fullname}
                        </div>
                      </td>
                      <td style={{ ...td, color: "#64748b" }}>{u.username}</td>
                      <td style={{ ...td, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>{u.phonenumber || "—"}</td>
                      <td style={td}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            background:
                              u.role === "superadmin" ? "rgba(124,58,237,0.12)"
                              : u.role === "admin" ? "rgba(16,185,129,0.12)"
                              : "rgba(100,116,139,0.1)",
                            color:
                              u.role === "superadmin" ? "#6d28d9"
                              : u.role === "admin" ? "#047857"
                              : "#475569",
                          }}
                        >
                          {u.role}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Systems table */}
          <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: "0 0 12px 4px" }}>
            Systems
          </h3>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(33, 150, 243, 0.1)",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, rgba(33,150,243,0.06), rgba(25,118,210,0.04))" }}>
                  <th style={th}>System</th>
                  <th style={th}>Study</th>
                  <th style={th}>Status</th>
                  <th style={th}>Claimed by</th>
                  <th style={{ ...th, textAlign: "right" }}>Last check</th>
                  <th style={{ ...th, textAlign: "right" }}>Version</th>
                </tr>
              </thead>
              <tbody>
                {detail.systems.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: "center", color: "#94a3b8" }}>
                      No systems registered in this org yet.
                    </td>
                  </tr>
                ) : (
                  detail.systems.map((s) => (
                    <tr key={s.id} style={{ borderTop: "1px solid rgba(33,150,243,0.06)" }}>
                      <td style={{ ...td, fontWeight: 600, color: "#1e293b" }}>{s.system_name || "—"}</td>
                      <td style={td}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            background: s.has_study ? "rgba(16,185,129,0.12)" : "rgba(100,116,139,0.1)",
                            color: s.has_study ? "#047857" : "#64748b",
                          }}
                        >
                          {s.has_study ? "Active" : "None"}
                        </span>
                      </td>
                      <td style={td}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            color: statusColor(s.status),
                            fontWeight: 600,
                            fontSize: "12px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: statusColor(s.status),
                            }}
                          />
                          {s.status || "online"}
                        </span>
                      </td>
                      <td style={{ ...td, color: s.claimedby ? "#1e293b" : "#94a3b8" }}>{s.claimedby_name || "—"}</td>
                      <td style={{ ...td, textAlign: "right", color: "#64748b" }}>{formatLastChecked(s.last_checked)}</td>
                      <td style={{ ...td, textAlign: "right", color: "#64748b", fontFamily: "monospace", fontSize: "12px" }}>{s.version || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 18px",
  fontSize: "11px",
  fontWeight: 700,
  color: "#1565C0",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const td: React.CSSProperties = {
  padding: "14px 18px",
  fontSize: "13px",
  color: "#374151",
};

export default SuperAdminDashboard;
